import { memo } from 'react';
import type { FC } from 'react';
import { cn } from '../../lib/utils';
import type { RangeSelection, TranslationState } from './types';

interface TokenSpanProps {
  text: string;
  nodeIndex: number;
  selection?: RangeSelection;
  isRangeHidden: boolean;
  isMuted?: boolean;
  /** When true, show original text with muted badge instead of translation */
  hidden?: boolean;
  handlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
  };
}

function getBadgeClass(state: TranslationState, isPhrase: boolean): string {
  switch (state) {
    case 'loading':
      return 'bg-secondary text-secondary-foreground';
    case 'translated':
      return isPhrase
        ? 'bg-purple text-purple-foreground'
        : 'bg-cyan text-cyan-foreground';
    case 'empty':
      return 'bg-cyan text-cyan-foreground';
    case 'untranslatable':
      return 'bg-warning text-warning-foreground';
    case 'notTranslated':
      return 'bg-secondary text-secondary-foreground';
  }
}

const TokenSpan: FC<TokenSpanProps> = ({
  text,
  selection,
  isRangeHidden,
  isMuted,
  hidden,
  handlers,
}) => {
  if (isRangeHidden) return null;

  // All tokens reserve identical space: transparent 1px border + padding.
  // Selections swap the border/background color — no layout shift.
  const baseClass = 'inline rounded border border-transparent touch-manipulation select-none';
  const selectedPadding = 'px-1.5 mx-px';
  const unselectedPadding = 'px-0';

  if (selection) {
    // Hidden mode: show original text with muted (secondary) background,
    // matching the in-flight translation visual treatment. For phrase
    // selections, render the whole joined phrase (selection.text) as a
    // single span — mirrors the translated-mode rendering so the phrase
    // stays visually cohesive instead of fragmenting into per-word badges.
    if (hidden) {
      const isPhrase = selection.startIndex !== selection.endIndex;
      return (
        <span
          className={cn(
            baseClass,
            selectedPadding,
            'bg-secondary text-secondary-foreground',
            isMuted && 'opacity-20'
          )}
          style={{ wordSpacing: '0.05em' }}
          {...handlers}
        >
          {isPhrase ? selection.text : text}
        </span>
      );
    }

    const displayText = selection.translation && selection.translationState === 'translated'
      ? selection.translation
      : selection.text;

    return (
      <span
        className={cn(
          baseClass,
          selectedPadding,
          getBadgeClass(selection.translationState, selection.startIndex !== selection.endIndex),
          isMuted && 'opacity-20'
        )}
        style={{ wordSpacing: '0.05em' }}
        {...handlers}
      >
        {displayText}
      </span>
    );
  }

  return (
    <span
      className={cn(
        baseClass,
        unselectedPadding,
        isMuted && 'opacity-20'
      )}
      {...handlers}
    >
      {text}
    </span>
  );
};

export default memo(TokenSpan);
